import {
  motion as motionLib,
  useReducedMotion,
  type Transition,
  type VariantLabels,
  type Variants,
} from "motion/react";

const VARIANT_HIDDEN: VariantLabels & string = "hidden" as const;
const VARIANT_ENTER: VariantLabels & string = "enter" as const;
const VARIANT_EXIT: VariantLabels & string = "exit" as const;

export type Motion = typeof motionLib;
export const motion: Motion = motionLib;

export { useReducedMotion };

export const useAgentReducedMotion = (): boolean => {
  const windowReduced =
    globalThis.window?.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  return useReducedMotion() === true || windowReduced;
};

const DURATIONS = {
  instant: 0.12,
  fast: 0.18,
  base: 0.24,
  slow: 0.32,
  slower: 0.44,
} as const;

const EASES = {
  out: [0.16, 1, 0.3, 1],
  in: [0.7, 0, 0.84, 0],
  inOut: [0.65, 0, 0.35, 1],
} satisfies Record<string, NonNullable<Transition["ease"]>>;

function transition(
  opts?: Partial<
    Transition & {
      easeName?: keyof typeof EASES;
      durationName?: keyof typeof DURATIONS;
    }
  >,
): Transition {
  if (!opts) {
    return { duration: DURATIONS.base, ease: EASES.out };
  }
  const { easeName, durationName, ...transitionOptions } = opts;
  const ease = easeName ? EASES[easeName] : undefined;
  const duration = durationName ? DURATIONS[durationName] : undefined;
  return {
    duration: duration ?? transitionOptions.duration ?? DURATIONS.base,
    ease: ease ?? transitionOptions.ease ?? EASES.out,
    ...transitionOptions,
  };
}

function defineVariants(v: Variants): Variants {
  return v;
}

/** Lotus template-picker timing, scaled down so the operator can type immediately. */
export const AGENT_EMPTY_ENTRANCE_TIMING = {
  gradientDelayMs: 200,
  gradientDurationMs: 800,
  greetingDelayMs: 500,
  composerDelayMs: 650,
  suggestionsBaseDelayMs: 800,
  suggestionsStaggerMs: 100,
  contentDurationMs: 600,
  contentOffsetPx: 20,
  composerOffsetPx: 24,
} as const;

export const agentEmptyEntranceTotalMs = (suggestionCount: number): number => {
  const lastSuggestionDelay =
    AGENT_EMPTY_ENTRANCE_TIMING.suggestionsBaseDelayMs +
    Math.max(0, suggestionCount - 1) * AGENT_EMPTY_ENTRANCE_TIMING.suggestionsStaggerMs;
  return lastSuggestionDelay + AGENT_EMPTY_ENTRANCE_TIMING.contentDurationMs;
};

export const agentEmptyEntranceContentVariants = (offsetPx: number): Variants =>
  defineVariants({
    [VARIANT_HIDDEN]: { opacity: 0, y: offsetPx },
    [VARIANT_ENTER]: {
      opacity: 1,
      y: 0,
      transition: transition({
        duration: AGENT_EMPTY_ENTRANCE_TIMING.contentDurationMs / 1000,
        ease: "easeOut",
      }),
    },
  });

export const agentEmptyEntranceGradientVariants = defineVariants({
  [VARIANT_HIDDEN]: { opacity: 0 },
  [VARIANT_ENTER]: {
    opacity: 0.25,
    transition: transition({
      delay: AGENT_EMPTY_ENTRANCE_TIMING.gradientDelayMs / 1000,
      duration: AGENT_EMPTY_ENTRANCE_TIMING.gradientDurationMs / 1000,
      ease: "easeOut",
    }),
  },
});

export const agentEmptyEntranceMotionProps = {
  initial: VARIANT_HIDDEN,
  animate: VARIANT_ENTER,
  exit: VARIANT_EXIT,
} as const;
